import type {
    QuippyBuildingSession,
    QuippyMessage,
    QuippyThread,
} from '../models';
import { findThread, generateThreadTitle } from './helpers';

export function setDraftInSession(
    session: QuippyBuildingSession,
    chatId: string,
    draft: string
): QuippyBuildingSession {
    return {
        ...session,
        draftByChat: {
            ...session.draftByChat,
            [chatId]: draft,
        },
    };
}

export function addUserMessageToSession(
    session: QuippyBuildingSession,
    chatId: string,
    message: QuippyMessage
): QuippyBuildingSession {
    return updateThreadInSession(session, chatId, (thread, currentSession) => {
        const messages = [...thread.messages, message];

        return {
            session: {
                ...currentSession,
                draftByChat: {
                    ...currentSession.draftByChat,
                    [chatId]: '',
                },
            },
            thread: {
                ...thread,
                messages,
                lastMessage: message.content,
                title: generateThreadTitle(messages),
                timestamp: new Date(),
            },
        };
    });
}

export function addAssistantMessageToSession(
    session: QuippyBuildingSession,
    chatId: string,
    message: QuippyMessage
): QuippyBuildingSession {
    return updateThreadInSession(session, chatId, (thread, currentSession) => ({
        session: currentSession,
        thread: {
            ...thread,
            messages: [...thread.messages, message],
        },
    }));
}

export function updateAssistantMessageInSession(
    session: QuippyBuildingSession,
    chatId: string,
    messageId: string,
    update: Partial<
        Pick<
            QuippyMessage,
            | 'content'
            | 'status'
            | 'sources'
            | 'retryInput'
            | 'retrySelectedFileId'
            | 'packAction'
            | 'documentOptions'
            | 'selectedDocumentFileId'
            | 'documentSelectionStatus'
        >
    >
): QuippyBuildingSession {
    return updateThreadInSession(session, chatId, (thread, currentSession) => {
        const messages = thread.messages.map((message) =>
            message.id === messageId
                ? {
                      ...message,
                      ...update,
                      sources: update.sources ?? message.sources,
                      documentOptions:
                          update.documentOptions ?? message.documentOptions,
                  }
                : message
        );

        const updatedMessage = messages.find(
            (message) => message.id === messageId
        );
        const lastMessage = messages.at(-1);
        const shouldUpdateMeta =
            !!updatedMessage &&
            updatedMessage.status === 'sent' &&
            lastMessage?.id === messageId;

        return {
            session: currentSession,
            thread: {
                ...thread,
                messages,
                lastMessage: shouldUpdateMeta
                    ? updatedMessage.content
                    : thread.lastMessage,
                title: shouldUpdateMeta
                    ? generateThreadTitle(messages)
                    : thread.title,
                timestamp: shouldUpdateMeta ? new Date() : thread.timestamp,
            },
        };
    });
}

export function resendEditedUserMessageInSession(
    session: QuippyBuildingSession,
    chatId: string,
    messageId: string,
    content: string,
    pendingMessage: QuippyMessage
): QuippyBuildingSession {
    return updateThreadInSession(session, chatId, (thread, currentSession) => {
        const messageIndex = thread.messages.findIndex(
            (message) => message.id === messageId
        );
        const message = thread.messages[messageIndex];
        if (message?.role !== 'user') {
            return { session: currentSession, thread };
        }

        const editedMessage: QuippyMessage = {
            ...message,
            content,
        };
        const messages = [
            ...thread.messages.slice(0, messageIndex),
            editedMessage,
            pendingMessage,
        ];

        return {
            session: {
                ...currentSession,
                draftByChat: {
                    ...currentSession.draftByChat,
                    [chatId]: '',
                },
            },
            thread: {
                ...thread,
                messages,
                lastMessage: content,
                title: generateThreadTitle(messages),
                timestamp: new Date(),
            },
        };
    });
}

function updateThreadInSession(
    session: QuippyBuildingSession,
    chatId: string,
    updater: (
        thread: QuippyThread,
        session: QuippyBuildingSession
    ) => { session: QuippyBuildingSession; thread: QuippyThread }
): QuippyBuildingSession {
    const thread = findThread(session, chatId);
    if (!thread) return session;

    const next = updater(thread, session);
    return {
        ...next.session,
        threads: next.session.threads.map((item) =>
            item.id === chatId ? next.thread : item
        ),
    };
}
